/**
 * @fileoverview A component that provides a share button with a popover for displaying a disabled hint.
 * It uses the `@headlessui/react` library for the popover and transition.
 *
 * @example
 * <ShareButton Icon={ShareIcon} onClick={handleShareClick} children="Share" />
 */

import { Popover, Transition } from '@headlessui/react'
import { Fragment, useState } from 'react'
import CheckIcon from '../assets/share/check.svg'


/**
 * @interface ShareButtonProps
 * @description Props for the ShareButton component.
 * @property {boolean} isDisabled - Whether the button is disabled.
 * @property {string} disabledHint - The hint to display when the button is disabled.
 * @property {boolean} disableAfterClick - Whether the button should be disabled after it is clicked.
 * @property {React.ComponentType<React.SVGAttributes<SVGElement>>} Icon - The icon to display on the button.
 * @property {() => void} onClick - The function to call when the button is clicked.
 * @property {React.ReactNode} children - The content to display on the button.
 */
interface ShareButtonProps {
    isDisabled?: boolean
    disabledHint?: string
    disableAfterClick?: boolean
    Icon: React.ComponentType<React.SVGAttributes<SVGElement>>
    onClick: () => void
    children: React.ReactNode
}

/**
 * @function ShareButton
 * @description Component for providing a share button with a popover for displaying a disabled hint.
 * @param {ShareButtonProps} props - The props for the ShareButton component.
 * @returns {JSX.Element | null} - The ShareButton component.
 */
export function ShareButton({
    isDisabled,
    onClick,
    Icon,
    children,
    disabledHint,
    disableAfterClick = false,
}: ShareButtonProps) {
    const [isOpen, setIsOpen] = useState(false)
    const [buttonClicked, setButtonClicked] = useState(false)

    function onButtonClick() {
        onClick()
        if (disableAfterClick) {
            setButtonClicked(true)
        }
    }

    if (buttonClicked) {
        return (
            <button
                type="button"
                disabled
                className="flex flex-col items-center justify-center rounded-md border border-indigo-50 bg-indigo-50 px-4 py-2.5 text-sm font-medium text-indigo-500 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all"
            >
                <CheckIcon className="fill-indigo-500" />
                {children}
            </button>
        )
    }

    if (!disabledHint || !isDisabled) {
        return (
            <button
                type="button"
                disabled={isDisabled}
                onClick={onButtonClick}
                className="flex flex-col items-center justify-center rounded-md border border-gray-300 disabled:border-gray-100 bg-white px-4 py-2.5 text-sm font-medium disabled:text-gray-400 text-gray-700 shadow-sm disabled:bg-gray-100 hover:enabled:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all"
            >
                <Icon />
                {children}
            </button>
        )
    }

    return (
        <div
            className="relative"
            onMouseEnter={() => setIsOpen(true)}
            onMouseLeave={() => setIsOpen(false)}
        >
            <Popover as={Fragment}>
                <Popover.Button as="span">
                    <button
                        type="button"
                        disabled={isDisabled}
                        onClick={onClick}
                        className="flex flex-col items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium disabled:text-gray-400 disabled:border-gray-100 text-gray-700 enabled:shadow-sm disabled:bg-gray-100 hover:enabled:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all"
                    >
                        <Icon className="opacity-50" />
                        {children}
                    </button>
                </Popover.Button>

                <Transition
                    show={isOpen}
                    className="absolute -top-[70px] -ml-[60px] z-10"
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                >
                    <Popover.Panel
                        static
                        className="bg-gray-900 py-2 px-3 w-[200px] origin-top-right rounded-md"
                    >
                        <p className="text-gray-100 text-xs font-normal leading-4">
                            {disabledHint}
                        </p>
                    </Popover.Panel>
                </Transition>
            </Popover>
        </div>
    )
}
